import SortIcon from "@mui/icons-material/ArrowUpward";
import FilterIcon from "@mui/icons-material/FilterAlt";
import {
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";
import { observer } from "mobx-react";
import * as React from "react";
import { Waypoint } from "react-waypoint";
import * as api from "@crochik/pi-api";
import { NumberFieldOptionsStyle } from "@crochik/pi-api";
import * as expr from "../../../context/Expression";
import { Action, TYPE } from "../../../context/IForm";
import ActionButton from "../ActionButton";
import { DataGridRow } from "./DataGridRow";
import { FilterComponent } from "./FilterComponent";
import { IDataViewComponentProps } from "./IDataViewComponentProps";
import { LocalFilterComponent } from "./LocalFilterComponent";

interface State {
  filterElement?: Element;
  filterField?: string;
  delayedUpdate?: {
    field: api.FormField;
    value: string | string[] | undefined;
  };
}

export const DataGridComponent1 = observer((props: IDataViewComponentProps) => {
  const { view, onClick, onObjectMenu } = props;

  const [state, setState] = React.useState<State>({});
  const { filterElement, filterField, delayedUpdate } = state;

  const onFilter = React.useCallback(
    (field: api.FormField, value: any) => {
      if (!field.name) return;

      if (value === undefined) {
        view.removeFilter(field.name);
        return;
      }

      view.setFilter(field.name, value);
    },
    [view],
  );

  const closeFilterPopover = React.useCallback(
    (closeWithoutUpdate?: boolean) => {
      if (!closeWithoutUpdate && !!delayedUpdate?.field) {
        onFilter(delayedUpdate.field, delayedUpdate.value);
      }

      setState((prev) => ({
        ...prev,
        filterElement: undefined,
        delayedUpdate: undefined,
      }));
    },
    [delayedUpdate, onFilter],
  );

  const sort = React.useCallback(
    (field: string) => (event: React.MouseEvent) => {
      event.stopPropagation();
      view.sortByField(field);
    },
    [view],
  );

  const filter = React.useCallback(
    (field: string) => (event: React.MouseEvent) => {
      event.stopPropagation();
      setState((prev) => ({
        ...prev,
        filterElement: event?.target as Element,
        filterField: field,
      }));
    },
    [],
  );

  const renderHeadCol = React.useCallback(
    (field: api.FormField) => {
      var align: "inherit" | "left" | "center" | "right" | "justify" =
        "inherit";

      switch (field.type) {
        case TYPE.NUMBER:
          if (field.options && "style" in field.options) {
            const options = field.options as api.NumberFieldOptions;
            switch (options.style) {
              case NumberFieldOptionsStyle.Currency:
                align = "right";
                break;

              case NumberFieldOptionsStyle.Price:
              case NumberFieldOptionsStyle.Rating:
              default:
                break;
            }
          } else {
            // by default aligns to the right
            align = "right";
          }
          break;

        case TYPE.LOCATIONDISTANCE:
          align = "right";
          break;

        default:
          break;
      }

      const isFilterable = view.isFilterable(field);
      const isFiltered = isFilterable && view.isFiltered(field);
      const hasLookupValues = isFilterable && view.hasLocalLookup(field);
      const isSortable = view.isSortable(field);
      const isSorted = view.isSorted(field);
      const isActive = isFiltered || isSorted;
      const direction = isFiltered
        ? "desc"
        : isSorted
          ? view.isSortReversed
            ? "asc"
            : "desc"
          : "desc";
      const title = isFiltered
        ? "Filtered"
        : isSorted
          ? "Sorted"
          : isFilterable
            ? "Filter"
            : "Sort";
      const icon = isFiltered
        ? FilterIcon
        : isSorted
          ? SortIcon
          : isFilterable
            ? FilterIcon
            : SortIcon;
      const isEnabled = isSortable || isFilterable || hasLookupValues;
      const onClick = field.name
        ? isFilterable
          ? filter(field.name)
          : sort(field.name)
        : undefined;

      return (
        <TableCell key={field.name} align={align}>
          <Tooltip title={title} enterDelay={300}>
            <TableSortLabel
              active={isActive}
              disabled={!isEnabled}
              direction={direction}
              onClick={onClick}
              IconComponent={icon}
            >
              {field.label || field.name}
            </TableSortLabel>
          </Tooltip>
        </TableCell>
      );
    },
    [view, filter, sort],
  );

  const onSelectAllClick = React.useCallback(() => {
    const all = view.records.length === view.selectedCount;
    view.selectAll(!all);
  }, [view]);

  const onValueChange = React.useCallback(
    (
      field: api.FormField,
      value: any,
      keepOpen?: boolean,
      delayUpdate?: boolean,
    ) => {
      if (delayUpdate) {
        setState((prev) => ({ ...prev, delayedUpdate: { field, value } }));
      } else {
        onFilter(field, value);
        if (!keepOpen) {
          closeFilterPopover();
        }
      }
    },
    [onFilter, closeFilterPopover],
  );

  const onSort = React.useCallback(
    (field: api.FormField, reverse: boolean) => {
      if (!field.name) return;
      view.sortByField(field.name, reverse);
    },
    [view],
  );

  const onHideColumn = React.useCallback(
    (field: api.FormField) => {
      if (!!field?.name) {
        closeFilterPopover(true);
        view.toggleFieldVisibility(field.name);
      }
    },
    [view, closeFilterPopover],
  );

  const onClosePopover = React.useCallback(
    () => closeFilterPopover(),
    [closeFilterPopover],
  );

  const onShowObjectMenu = React.useCallback(
    (row: object) => (e: React.MouseEvent) => {
      onObjectMenu?.(e.target as HTMLElement, row);
    },
    [onObjectMenu],
  );

  const renderHead = React.useCallback(() => {
    const { visibleFields, isFilterableLocally } = view;
    const rowCount = view.records.length;

    const filterComponent = !filterField ? null : isFilterableLocally ? (
      <LocalFilterComponent
        dataView={view}
        fieldName={filterField}
        renderAlways={true}
      />
    ) : view.filterForm ? (
      <FilterComponent
        filterForm={view.filterForm}
        fieldName={filterField}
        style={{ maxWidth: 400 }}
        onSelect={onValueChange}
        onSort={onSort}
        onClose={closeFilterPopover}
        onHideColumn={onHideColumn}
      />
    ) : null;

    const { canLoadMore, isSelectable, singleObjectActions } = view;
    const canSelectAll = !canLoadMore && rowCount > 0;

    return (
      <TableHead>
        <TableRow>
          {isSelectable && (
            <TableCell
              padding="none"
              size="small"
              style={{ width: 42, paddingLeft: 12 }}
            >
              {canSelectAll && (
                <Checkbox
                  indeterminate={
                    view.selectedCount > 0 && view.selectedCount < rowCount
                  }
                  checked={view.selectedCount === rowCount}
                  onChange={onSelectAllClick}
                />
              )}
            </TableCell>
          )}
          {singleObjectActions && (
            <TableCell padding="none" size="small" style={{ maxWidth: 42 }} />
          )}
          {visibleFields.map((column) => renderHeadCol(column))}
        </TableRow>
        <Popover
          open={!!filterElement}
          anchorEl={filterElement}
          onClose={onClosePopover}
          transitionDuration="auto"
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          {filterComponent}
        </Popover>
      </TableHead>
    );
  }, [
    view,
    filterElement,
    filterField,
    onValueChange,
    onSort,
    closeFilterPopover,
    onHideColumn,
    onSelectAllClick,
    renderHeadCol,
    onClosePopover,
  ]);

  // const createFieldComponent = React.useCallback((props: FieldProps): JSX.Element => {
  //     switch (props.field.type) {
  //         case TYPE.TEXT:
  //             return <TextField {...props} />;

  //         case TYPE.PASSWORD:
  //             return <PasswordField {...props} />;

  //         case TYPE.ADDRESS:
  //             return <AddressField {...props} />;

  //         case TYPE.EMAIL:
  //             return <EmailField {...props} />;

  //         case TYPE.PHONE:
  //             return <PhoneField {...props} />;

  //         case TYPE.POSTALCODE:
  //         case "postalCode":
  //             return <PostalCodeField {...props} />;

  //         default:
  //             return <span>unrecognized type: "{props.field.type}"</span>;
  //     }
  // }, []);

  const onSelect = React.useCallback(
    (e: React.MouseEvent, row: object) => {
      // handle selection (if selectable)
      view.select(row);
    },
    [view],
  );

  const onFilterChange = React.useCallback(
    (field: api.FormField) => (value: object) => {
      if (!view || !field.name) return;
      view.setFilter(field.name, value);
    },
    [view],
  );

  const renderBody = React.useCallback(() => {
    if (!view.records) return;

    const loadedFields: { [name: string]: boolean } = {};
    view.loadedFields?.forEach((x) => (loadedFields[x] = true));

    const data = view.records;

    return (
      <TableBody>
        {data.map((id) => {
          const row = view.get(id);
          return (
            <DataGridRow
              key={id}
              loadedFields={loadedFields}
              row={row}
              view={view}
              onClick={onClick}
              onSelect={onSelect}
              onFilterChange={onFilterChange}
              onPopup={onShowObjectMenu(row)}
            />
          );
        })}
      </TableBody>
    );
  }, [view, onClick, onSelect, onFilterChange, onShowObjectMenu]);

  const isDisabled = React.useCallback(
    (ui: api.UIElement): boolean => {
      return !expr.evaluate(ui.enable, view.context);
    },
    [view],
  );

  const isHidden = React.useCallback(
    (ui: api.UIElement): boolean => {
      return !expr.evaluate(ui.visible, view.context);
    },
    [view],
  );

  const renderAction = React.useCallback(
    (action: Action): JSX.Element | undefined => {
      const disabled = isDisabled(action);

      return (
        <ActionButton
          key={action.name}
          title={action.label ?? action.name ?? "[no-name]"}
          color="primary"
          variant="contained"
          disabled={disabled}
          action={action.action ?? action.name ?? "[no-name]"}
          context={view.context}
        />
      );
    },
    [view, isDisabled],
  );

  const loadMore = React.useCallback(() => {
    view.loadMoreAsync();
  }, [view]);

  // const renderFooter = React.useCallback((): React.ReactNode => {
  //     var actions: Action[] = [];

  //     if (view.actions) {
  //         for (var action of view.actions) {
  //             if (!isHidden(action)) {
  //                 actions.push(action);
  //             }
  //         }
  //     }

  //     if (actions.length < 1) {
  //         return undefined;
  //     }

  //     return (
  //         <TableFooter>
  //             <TableRow>
  //                 <TableCell colSpan={columns}>{actions.map((action) => renderAction(action))}</TableCell>
  //             </TableRow>
  //         </TableFooter>
  //     );
  // }, [view, isHidden, columns, renderAction]);

  const renderActions = React.useCallback((): React.ReactNode => {
    var actions: Action[] = [];

    if (view.actions) {
      for (var action of view.actions) {
        if (!isHidden(action)) {
          actions.push(action);
        }
      }
    }

    if (actions.length < 1) {
      return undefined;
    }

    return <div>{actions.map((action) => renderAction(action))}</div>;
  }, [view, isHidden, renderAction]);

  return (
    <Paper
      sx={{
        overflow: "auto",
        height: "100%",
        marginTop: "1px",
      }}
    >
      <Table stickyHeader={true} size="small">
        {renderHead()}
        {renderBody()}
      </Table>
      {!view.isLoading && view.canLoadMore && (
        <Waypoint bottomOffset="-300px" onEnter={loadMore} />
      )}
      {renderActions()}
    </Paper>
  );
});
